import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';
import cm from 'classnames';
import { Button } from 'ksnm-common-ui/lib/Button';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { Tree } from 'ksnm-common-ui/lib/Tree';

import { DesignLayout } from '@common/DesignLayout';
import { RoutePath } from '@components/AppRouter/constants';
import { useCheckFromDictionaryCard } from '@src/utils/hooks/useCheckFromDictionaryCard';
import { usePermission } from '@src/utils/hooks/usePermission';
import { recursiveFindByCode } from '@src/utils/tree';
import { controlGroupsStore } from '@stores/dictionaryStores/controlGroupsStore/controlGroupsStore';
import { controlsStore } from '@stores/dictionaryStores/controlsStore/controlsStore';
import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';
import { reportGroupsStore } from '@stores/dictionaryStores/reportGroupsStore/reportGroupsStore';

import { NEW_ENTITY_ID } from '../../../AppRouter/hooks/constants';
import { useAuditView } from '../../hooks/useAuditView';
import { Filter } from './Filter';
import { Table } from './Table';
import { toTreeView } from './toTreeView';

import styles from './Controls.module.scss';

const ControlsComponent = () => {
  const {
    fetch,
    controls,
    filters,
    setBreadcrumbs,
    fullSize,
    selectNode,
    setSelectNode,
    selectedKeys,
    setSelectedKeys,
    expandedKeys,
    setExpandedKeys,
    clear,
  } = controlsStore;
  const { push } = useHistory();
  const { reportGroups } = reportGroupsStore;
  const { controlGroups } = controlGroupsStore;
  const { state } = useLocation();

  const permission = usePermission('CmControl');

  const isBack = useCheckFromDictionaryCard(RoutePath.DICTIONARY_CONTROLS);

  useAuditView('Контроли');

  useEffect(() => {
    fetch();
    fetchReferences(['reportGroups', 'controlGroups']);
  }, [fetch]);

  useEffect(() => {
    if (!isBack) clear();
  }, [clear, isBack]);

  const treeData = useMemo(() => {
    const out = controls.map((item) => {
      const reportGroupName = reportGroups?.find((group) => group.code === item.repGroupCode)?.name;
      const controlGroup = controlGroups?.find((group) => group.ctrlGroupCode === item.ctrlGroupCode);

      return {
        ...item,
        reportGroupName,
        ctrlGroupName: controlGroup?.ctrlGroupName,
        ctrlGroupOrder: controlGroup?.ctrlGroupOrder,
      };
    });

    return toTreeView(
      out.filter(
        (control) =>
          !(
            (filters.reportGroup && control.repGroupCode !== filters.reportGroup) ||
            (filters.controlGroup && control.ctrlGroupCode !== filters.controlGroup)
          ),
      ),
    );
  }, [controls, reportGroups, controlGroups, filters.reportGroup, filters.controlGroup]);

  const onSelect = useCallback(
    (_, { node, selected }) => {
      if (selected) {
        setSelectNode(node);
        setSelectedKeys([node.key]);

        const nodes = recursiveFindByCode(node.key, treeData);
        if (nodes) setBreadcrumbs(nodes.map((item) => item.title));
      }
    },
    [setBreadcrumbs, setSelectNode, setSelectedKeys, treeData],
  );

  // при переходе к контролям из справочника Группы контролей, открываем иерархию по группе из которой осуществлялся переход
  useEffect(() => {
    if (state && state.ctrlGroupCode) {
      const stateControl = controls.find((control) => control.ctrlGroupCode === state.ctrlGroupCode);

      if (stateControl) {
        setExpandedKeys([stateControl.repGroupCode ?? '']);
        onSelect(null, {
          node: { ...stateControl, key: `${stateControl.repGroupCode} - ${stateControl.ctrlGroupCode}` },
          selected: true,
        });
      }
    }
  }, [controls, onSelect, setExpandedKeys, state]);

  const onExpand = useCallback(
    (keys) => {
      setExpandedKeys(keys);
    },
    [setExpandedKeys],
  );

  const onCreate = useCallback(() => {
    push(`${RoutePath.DICTIONARY_CONTROLS}/${NEW_ENTITY_ID}`);
  }, [push]);

  const actions = useMemo(
    () =>
      !selectNode && permission.CREATE ? (
        <Button onClick={onCreate} tooltip="Добавить">
          <Icon name="add" />
        </Button>
      ) : null,
    [onCreate, permission.CREATE, selectNode],
  );

  return (
    <DesignLayout scrollEnable>
      <Filter />
      <div className={styles.container}>
        <Tree
          selectable
          treeData={treeData}
          onSelect={onSelect}
          className={cm(styles.tree, { [styles.withTable]: selectNode, [styles.fullSize]: fullSize })}
          selectedKeys={selectedKeys}
          expandedKeys={expandedKeys}
          onExpand={onExpand}
          actions={actions}
        />
        <Table />
      </div>
    </DesignLayout>
  );
};

export const Controls = observer(ControlsComponent);
